import type { ShipTier } from "@voidex/contracts-ts";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { useActiveFarmSessions, useShipsByTier, useStartFarm } from "../../hooks";
import { formatZoneDuration, formatZoneMultiplier, getZoneColor } from "../../services/zoneUtils";
import { getZoneProof } from "../../services/zoneMerkle";
import { useSpaceStore } from "../../stores";
import { useTransactionStore } from "../../stores/transactionStore";
import { LoadingButton } from "../shared/LoadingButton";
import { StepIndicator } from "../shared/StepIndicator";
import { ApprovalStep } from "./ApprovalStep";
import { ShipSelector } from "./ShipSelector";

const WIZARD_STEPS = ["Select Ship", "Approve & Launch"];

interface FarmingWizardProps {
	onLaunched?: (shipTier: ShipTier) => void;
}

export function FarmingWizard({ onLaunched }: FarmingWizardProps) {
	const { address } = useAccount();
	const addToast = useTransactionStore((s) => s.addToast);
	const farmingTarget = useSpaceStore((s) => s.farmingTarget);

	const {
		ships: allShips,
		isLoading: isShipsLoading,
		refetch: refetchShips,
	} = useShipsByTier(address);
	const { farmingShipIds, refetch: refetchSessions } = useActiveFarmSessions(address);
	const { startFarm, isPending: isLaunching, isSuccess: launchSuccess } = useStartFarm();

	// Get zone proof for the target system
	const zoneProofData = useMemo(() => {
		if (!farmingTarget) return null;
		try {
			return getZoneProof(farmingTarget.system.systemIndex);
		} catch {
			return null;
		}
	}, [farmingTarget]);

	// Optimistically track ships just launched (subgraph lags behind on-chain)
	const [launchedShipIds, setLaunchedShipIds] = useState<Set<bigint>>(new Set());
	const [isSyncing, setIsSyncing] = useState(false);

	// Filter out ships in active sessions AND just-launched ships
	const ships = allShips.filter(
		(s) => !farmingShipIds.has(s.tokenId) && !launchedShipIds.has(s.tokenId),
	);

	const [step, setStep] = useState(0);
	const [selectedShipId, setSelectedShipId] = useState<bigint | null>(null);

	function handleLaunch(): void {
		if (selectedShipId === null || !farmingTarget || !zoneProofData) return;
		startFarm(
			selectedShipId,
			farmingTarget.system.systemIndex,
			zoneProofData.zone,
			zoneProofData.proof,
		);
	}

	// Track the ship being launched so we can optimistically hide it
	const launchingShipRef = useRef<bigint | null>(null);
	const launchingTierRef = useRef<ShipTier | null>(null);
	if (selectedShipId !== null && isLaunching) {
		launchingShipRef.current = selectedShipId;
		launchingTierRef.current = allShips.find((s) => s.tokenId === selectedShipId)?.tier ?? null;
	}

	// Reset wizard on successful launch
	useEffect(() => {
		if (!launchSuccess) return;

		// Optimistically hide the launched ship (subgraph hasn't indexed yet)
		const launchedShip = launchingShipRef.current;
		if (launchedShip !== null) {
			setLaunchedShipIds((prev) => new Set([...prev, launchedShip]));
			launchingShipRef.current = null;
		}

		// Notify parent (e.g. modal) — it takes over UX with animation
		if (onLaunched && launchingTierRef.current !== null) {
			onLaunched(launchingTierRef.current);
		} else if (!onLaunched) {
			addToast({
				type: "success",
				title: "Mission Launched",
				message: "Your farming mission has been started. VRF will determine the outcome.",
			});
		}

		setStep(0);
		setSelectedShipId(null);

		// Show syncing skeleton while waiting for subgraph indexer
		setIsSyncing(true);

		// Delayed refetch for subgraph data (give indexer time to catch up)
		const timer = setTimeout(() => {
			refetchShips();
			refetchSessions();
			// Clear optimistic state once subgraph has caught up
			setLaunchedShipIds(new Set());
			setIsSyncing(false);
		}, 5000);
		return () => clearTimeout(timer);
	}, [launchSuccess, addToast, refetchShips, refetchSessions, onLaunched]);

	const zone = farmingTarget?.system.zone ?? 0;

	return (
		<div className="farming-wizard">
			{isSyncing && (
				<div className="farming-wizard__syncing">
					<div className="farming-wizard__syncing-spinner" />
					<span>Syncing on-chain data...</span>
				</div>
			)}

			{/* Zone info banner */}
			<div className="farming-wizard__zone" style={{ borderColor: getZoneColor(zone) }}>
				<span className="farming-wizard__zone-badge" style={{ background: getZoneColor(zone) }}>
					Zone {zone}
				</span>
				<span className="farming-wizard__zone-detail">{formatZoneDuration(zone)} farming</span>
				<span className="farming-wizard__zone-detail">{formatZoneMultiplier(zone)} rewards</span>
			</div>

			<div className="farming-wizard__steps">
				<StepIndicator steps={WIZARD_STEPS} currentStep={step} />
			</div>

			<div className="farming-wizard__content">
				{step === 0 && (
					<ShipSelector
						ships={ships}
						selectedShipId={selectedShipId}
						onSelect={setSelectedShipId}
						isLoading={isShipsLoading}
					/>
				)}

				{step === 1 && (
					<ApprovalStep
						selectedShipId={selectedShipId}
						onLaunch={handleLaunch}
						isLaunching={isLaunching}
					/>
				)}
			</div>

			<div className="farming-wizard__actions">
				{step > 0 && (
					<LoadingButton onClick={() => setStep(step - 1)} variant="secondary">
						Back
					</LoadingButton>
				)}

				{step < 1 && (
					<LoadingButton onClick={() => setStep(step + 1)} disabled={selectedShipId === null}>
						Next
					</LoadingButton>
				)}
			</div>
		</div>
	);
}
